import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import PostCard from './components/PostCard';
import StoryHighlights from './components/StoryHighlights';
import CategoryFilter from './components/CategoryFilter';
import TrendingTopics from './components/TrendingTopics';
import CreatePostModal from './components/CreatePostModal';
import UserSuggestions from './components/UserSuggestions';
import SearchBar from './components/SearchBar';

const Community = () => {
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFilters, setSearchFilters] = useState({});

  // Mock data
  const mockPosts = [
    {
      id: 1,
      author: {
        name: "Sarah Chen",
        avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
        isVerified: true,
        badge: "Gift Expert"
      },
      title: "Perfect Anniversary Gift Success Story!",
      content: `After 5 years together, I wanted to give my partner something truly special for our anniversary. I spent weeks researching and finally decided on a custom star map showing the night sky from our first date.\n\nThe reaction was absolutely priceless! They started crying happy tears and said it was the most thoughtful gift they'd ever received. The personal touch really made all the difference.\n\nFor anyone looking for anniversary ideas, I highly recommend something that captures a special memory you both share. It doesn't have to be expensive - it just needs to come from the heart.`,
      category: "Gift Wins",
      tags: ["anniversary", "personalized", "romantic", "custom"],
      images: [
        "https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=400&h=300&fit=crop",
        "https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=400&h=300&fit=crop"
      ],
      giftDetails: {
        recipient: "Partner",
        occasion: "Anniversary",
        budget: "$50 - $100"
      },
      timestamp: new Date(Date.now() - 3600000),
      likesCount: 127,
      commentsCount: 23,
      isLiked: false,
      isSaved: false
    },
    {
      id: 2,
      author: {
        name: "Michael Rodriguez",
        avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
        isVerified: false,
        badge: null
      },
      title: "Need Help: Last-Minute Birthday Gift for Mom",
      content: `My mom's 60th birthday is tomorrow and I completely forgot! 😅 She's into gardening, cooking, and reading mystery novels. Budget is around $100-150.\n\nI'm thinking either a nice cookbook collection, some premium gardening tools, or maybe a subscription box? Has anyone had success with last-minute gifts that still felt thoughtful?\n\nAny suggestions would be greatly appreciated!`,
      category: "Need Advice",
      tags: ["birthday", "mom", "lastminute", "help"],
      images: [],
      giftDetails: {
        recipient: "Mother",
        occasion: "Birthday",
        budget: "$100 - $250"
      },
      timestamp: new Date(Date.now() - 7200000),
      likesCount: 45,
      commentsCount: 67,
      isLiked: true,
      isSaved: true
    },
    {
      id: 3,
      author: {
        name: "Emma Thompson",
        avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
        isVerified: true,
        badge: "DIY Master"
      },
      title: "DIY Memory Jar - Perfect for Any Occasion!",
      content: `I created this beautiful memory jar for my best friend's graduation and it was such a hit! Here's how I made it:\n\n1. Got a large mason jar and decorated it with ribbon\n2. Wrote 50+ memories we shared on colorful paper strips\n3. Added some photos and small mementos\n4. Included future adventure ideas for us to do together\n\nTotal cost was under $20 but the emotional value was priceless. She said it was better than any store-bought gift because it was so personal and showed how much our friendship means to me.`,
      category: "Inspiration Boards",
      tags: ["diy", "friendship", "graduation", "memories", "budget"],
      images: [
        "https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=400&h=300&fit=crop",
        "https://images.unsplash.com/photo-1606107557195-0e29a4b5b4aa?w=400&h=300&fit=crop",
        "https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=400&h=300&fit=crop"
      ],
      giftDetails: {
        recipient: "Best Friend",
        occasion: "Graduation",
        budget: "Under $25"
      },
      timestamp: new Date(Date.now() - 14400000),
      likesCount: 89,
      commentsCount: 34,
      isLiked: false,
      isSaved: false
    },
    {
      id: 4,
      author: {
        name: "David Park",
        avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
        isVerified: false,
        badge: "Thoughtful Giver"
      },
      title: "Thank You Community - Wedding Gift Success!",
      content: `I posted here a month ago asking for wedding gift ideas for my colleague. The community suggested a personalized cutting board with their names and wedding date.\n\nJust wanted to update everyone - they LOVED it! They sent me a photo of it in their new kitchen and said it's their favorite wedding gift. They use it every day and think of our team.\n\nThank you to everyone who helped brainstorm. This community is amazing! 💕`,
      category: "Thank You Moments",
      tags: ["wedding", "colleague", "personalized", "thankyou"],
      images: [
        "https://images.unsplash.com/photo-1606107557195-0e29a4b5b4aa?w=400&h=300&fit=crop"
      ],
      giftDetails: {
        recipient: "Colleague",
        occasion: "Wedding",
        budget: "$25 - $50"
      },
      timestamp: new Date(Date.now() - 21600000),
      likesCount: 156,
      commentsCount: 42,
      isLiked: true,
      isSaved: false
    }
  ];

  const mockHighlights = [
    {
      id: 1,
      title: "Holiday 2024",
      type: "seasonal",
      coverImage: "https://images.unsplash.com/photo-1512389142860-9c449e58a543?w=150&h=150&fit=crop",
      isNew: false
    },
    {
      id: 2,
      title: "DIY Ideas",
      type: "diy",
      coverImage: "https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=150&h=150&fit=crop",
      isNew: true
    },
    {
      id: 3,
      title: "Budget Wins",
      type: "budget",
      coverImage: null,
      isNew: false
    },
    {
      id: 4,
      title: "Trending",
      type: "trending",
      coverImage: "https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=150&h=150&fit=crop",
      isNew: false
    }
  ];

  const mockCategories = [
    { name: 'All', count: 1247 },
    { name: 'Gift Wins', count: 423 },
    { name: 'Need Advice', count: 312 },
    { name: 'Inspiration Boards', count: 298 },
    { name: 'Thank You Moments', count: 156 },
    { name: 'Cultural Celebrations', count: 58 }
  ];

  const mockTrendingTopics = [
    {
      id: 1,
      hashtag: "holidaygifts2024",
      postsCount: 1247,
      trend: 85
    },
    {
      id: 2,
      hashtag: "diygifts",
      postsCount: 892,
      trend: 42
    },
    {
      id: 3,
      hashtag: "budgetfriendly",
      postsCount: 756,
      trend: 28
    },
    {
      id: 4,
      hashtag: "personalized",
      postsCount: 634,
      trend: 15
    },
    {
      id: 5,
      hashtag: "lastminute",
      postsCount: 523,
      trend: -8
    }
  ];

  const mockUserSuggestions = [
    {
      id: 1,
      name: "Jessica Martinez",
      avatar: "https://images.unsplash.com/photo-1517841905240-472988babdf9?w=150&h=150&fit=crop&crop=face",
      bio: "Gift curator & lifestyle blogger",
      followersCount: 2847,
      postsCount: 156,
      isVerified: true,
      badge: "Curator"
    },
    {
      id: 2,
      name: "Alex Chen",
      avatar: "https://images.unsplash.com/photo-1519345182560-3f2917c472ef?w=150&h=150&fit=crop&crop=face",
      bio: "DIY enthusiast sharing creative ideas",
      followersCount: 1523,
      postsCount: 89,
      isVerified: false,
      badge: "DIY Expert"
    },
    {
      id: 3,
      name: "Maria Rodriguez",
      avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=face",
      bio: "Cultural celebration specialist",
      followersCount: 967,
      postsCount: 67,
      isVerified: true,
      badge: null
    }
  ];

  useEffect(() => {
    setPosts(mockPosts);
    setFilteredPosts(mockPosts);
  }, []);

  useEffect(() => {
    let filtered = posts;

    // Filter by category
    if (activeCategory !== 'All') {
      filtered = filtered.filter(post => post.category === activeCategory);
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(post => 
        post.title.toLowerCase().includes(query) ||
        post.content.toLowerCase().includes(query) ||
        post.tags.some(tag => tag.toLowerCase().includes(query)) ||
        post.author.name.toLowerCase().includes(query)
      );
    }

    // Apply search filters
    if (searchFilters.hasImages) {
      filtered = filtered.filter(post => post.images && post.images.length > 0);
    }

    if (searchFilters.verified) {
      filtered = filtered.filter(post => post.author.isVerified);
    }

    // Sort posts
    switch (searchFilters.sortBy) {
      case 'popular':
        filtered.sort((a, b) => b.likesCount - a.likesCount);
        break;
      case 'helpful':
        filtered.sort((a, b) => b.commentsCount - a.commentsCount);
        break;
      case 'trending':
        filtered.sort((a, b) => (b.likesCount + b.commentsCount) - (a.likesCount + a.commentsCount));
        break;
      default:
        filtered.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    }

    setFilteredPosts(filtered);
  }, [posts, activeCategory, searchQuery, searchFilters]);

  const handleLike = (postId, isLiked) => {
    setPosts(prev => prev.map(post => 
      post.id === postId 
        ? { ...post, isLiked, likesCount: isLiked ? post.likesCount + 1 : post.likesCount - 1 }
        : post
    ));
  };

  const handleComment = (postId) => {
    console.log('Comment on post:', postId);
  };

  const handleSave = (postId, isSaved) => {
    setPosts(prev => prev.map(post => 
      post.id === postId 
        ? { ...post, isSaved }
        : post
    ));
  };

  const handleShare = (postId) => {
    console.log('Share post:', postId);
  };

  const handleCreatePost = (postData) => {
    const newPost = {
      id: posts.length + 1,
      author: {
        name: "You",
        avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
        isVerified: false,
        badge: null
      },
      ...postData,
      timestamp: new Date(),
      likesCount: 0,
      commentsCount: 0,
      isLiked: false,
      isSaved: false
    };
    
    setPosts(prev => [newPost, ...prev]);
  };

  const handleHighlightClick = (highlight) => {
    console.log('View highlight:', highlight);
  };

  const handleTopicClick = (topic) => {
    setSearchQuery(`#${topic.hashtag}`);
  };

  const handleFollow = (userId) => {
    console.log('Follow user:', userId);
  };

  const handleViewProfile = (userId) => {
    console.log('View profile:', userId);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handleFilterChange = (filters) => {
    setSearchFilters(filters);
  };

  return (
    <div className="min-h-screen bg-surface">
      <Header />
      
      <div className="pt-20 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="text-center mb-8">
            <h1 className="font-heading font-bold text-3xl lg:text-4xl text-accent-800 mb-4">
              Gift Community
            </h1>
            <p className="text-lg text-accent-600 max-w-2xl mx-auto">
              Share your gift successes, get advice, and discover inspiration from fellow gift-givers
            </p>
          </div>

          {/* Story Highlights */}
          <div className="mb-8">
            <StoryHighlights 
              highlights={mockHighlights}
              onHighlightClick={handleHighlightClick}
            />
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Left Sidebar */}
            <div className="lg:col-span-1 space-y-6">
              {/* Search */}
              <SearchBar 
                onSearch={handleSearch}
                onFilterChange={handleFilterChange}
              />
              
              {/* Categories */}
              <CategoryFilter 
                categories={mockCategories}
                activeCategory={activeCategory}
                onCategoryChange={setActiveCategory}
              />
              
              {/* Trending Topics */}
              <TrendingTopics 
                topics={mockTrendingTopics}
                onTopicClick={handleTopicClick}
              />
            </div>

            {/* Main Feed */}
            <div className="lg:col-span-2">
              {/* Create Post Button */}
              <div className="mb-6">
                <Button
                  variant="primary"
                  fullWidth
                  iconName="Plus"
                  iconPosition="left"
                  onClick={() => setIsCreateModalOpen(true)}
                  className="h-12 text-lg shadow-warm hover:shadow-primary"
                >
                  Share Your Gift Story
                </Button>
              </div>

              {/* Posts Feed */}
              <div className="space-y-6">
                {filteredPosts.length > 0 ? (
                  filteredPosts.map((post) => (
                    <PostCard
                      key={post.id}
                      post={post}
                      onLike={handleLike}
                      onComment={handleComment}
                      onSave={handleSave}
                      onShare={handleShare}
                    />
                  ))
                ) : (
                  <div className="text-center py-12">
                    <Icon name="Search" size={48} className="text-accent-300 mx-auto mb-4" />
                    <h3 className="font-semibold text-lg text-accent-600 mb-2">No posts found</h3>
                    <p className="text-accent-500">
                      Try adjusting your search or filters to find more content
                    </p>
                  </div>
                )}
              </div>

              {/* Load More */}
              {filteredPosts.length > 0 && (
                <div className="text-center mt-8">
                  <Button
                    variant="ghost"
                    iconName="ChevronDown"
                    iconPosition="right"
                    className="text-accent-600"
                  >
                    Load More Posts
                  </Button>
                </div>
              )}
            </div>

            {/* Right Sidebar */}
            <div className="lg:col-span-1 space-y-6">
              {/* User Suggestions */}
              <UserSuggestions 
                suggestions={mockUserSuggestions}
                onFollow={handleFollow}
                onViewProfile={handleViewProfile}
              />

              {/* Community Stats */}
              <div className="bg-white rounded-2xl shadow-soft border border-accent-100 p-6">
                <h3 className="font-heading font-semibold text-lg text-accent-800 mb-4">
                  Community Stats
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-accent-600">Total Members</span>
                    <span className="font-semibold text-accent-800">12,847</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-accent-600">Posts This Week</span>
                    <span className="font-semibold text-accent-800">1,234</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-accent-600">Success Stories</span>
                    <span className="font-semibold text-accent-800">3,456</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-accent-600">Active Today</span>
                    <span className="font-semibold text-primary">2,891</span>
                  </div>
                </div>
              </div>

              {/* Community Guidelines */}
              <div className="bg-white rounded-2xl shadow-soft border border-accent-100 p-6">
                <h3 className="font-heading font-semibold text-lg text-accent-800 mb-4">
                  Community Guidelines
                </h3>
                <div className="space-y-3 text-sm text-accent-600">
                  <div className="flex items-start space-x-2">
                    <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                    <span>Be respectful and supportive</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                    <span>Share genuine experiences</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                    <span>Provide helpful advice</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                    <span>Respect privacy</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Post Modal */}
      <CreatePostModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreatePost}
      />
    </div>
  );
};

export default Community;